import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaSearch, FaTag } from "react-icons/fa";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { productActions } from "../../sagas/productSlice";
import { categoryActions } from "../../sagas/categorySlice";
import { formatPrice } from "../../helpers/utility";
import LabelGenerationModal from "./LabelGenerationModal";

const LabelGenerationPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [labelFormModal, setLabelFormModal] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState({});
    const [filters, setFilters] = useState({
        categorieId: null,
        query: "",
    });

    const { list: categories } = useSelector((state) => state.categories);
    const { unites, products, totalElements, totalPages, pageSize } = useSelector(
        (state) => state.products
    );

    useEffect(() => {
        searchInputRef.current?.focus();
        dispatch(categoryActions.list({ query: "" }));
        dispatch(productActions.findAllUnites());
    }, [dispatch]);

    useEffect(() => {
        dispatch(
            productActions.findAllArticles({
                ...filters,
                page: currentPage - 1,
                size: 10,
            })
        );
    }, [dispatch, currentPage, filters]);

    const selectedProductList = Object.values(selectedProducts);
    const areAllVisibleProductsSelected =
        (products || []).length > 0 &&
        (products || []).every((product) => Boolean(selectedProducts[product.id]));

    const toggleSelectProduct = (product) => {
        setSelectedProducts((prev) => {
            if (prev[product.id]) {
                const { [product.id]: removed, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [product.id]: product,
            };
        });
    };

    const toggleSelectVisibleProducts = (checked) => {
        setSelectedProducts((prev) => {
            if (!checked) {
                const next = { ...prev };
                (products || []).forEach((product) => {
                    delete next[product.id];
                });
                return next;
            }

            const next = { ...prev };
            (products || []).forEach((product) => {
                next[product.id] = product;
            });
            return next;
        });
    };

    return (
        <Card className="shadow-sm">
            <CardBody>
                <Row className="mb-3 g-2 align-items-end">
                    <Col md={5}>
                        <Input
                            innerRef={searchInputRef}
                            type="text"
                            placeholder={t("product.searchPlaceholder")}
                            value={filters.query}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    query: e.target.value,
                                }))
                            }
                            className="ps-4"
                        />
                    </Col>
                    <Col md={4}>
                        <Select
                            name="categorie"
                            options={categories.map((cat) => ({
                                value: cat.id,
                                label: cat.libelle,
                            }))}
                            value={
                                categories
                                    .map((cat) => ({ value: cat.id, label: cat.libelle }))
                                    .find((option) => option.value === filters.categorieId) || null
                            }
                            onChange={(selected) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    categorieId: selected ? selected.value : null,
                                }))
                            }
                            placeholder="Rechercher une catégorie..."
                            isClearable
                            isSearchable
                        />
                    </Col>
                    <Col md={3} className="d-flex justify-content-end">
                        <Button
                            color="primary"
                            onClick={() => setLabelFormModal(true)}
                            disabled={selectedProductList.length === 0}
                        >
                            <FaBarcode className="me-2" />
                            Generer tickets
                        </Button>
                    </Col>
                </Row>

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <small className="text-muted">
                        {selectedProductList.length} article(s) sélectionné(s)
                    </small>
                    <div className="d-flex align-items-center gap-2">
                        <Button color="light" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                            <FaAngleDoubleLeft />
                        </Button>
                        <Button color="light" size="sm" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                            <FaAngleLeft />
                        </Button>
                        <span className="mx-2">
                            {currentPage} / {totalPages}
                        </span>
                        <Button color="light" size="sm" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                            <FaAngleRight />
                        </Button>
                        <Button color="light" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                            <FaAngleDoubleRight />
                        </Button>
                    </div>
                </div>

                <Table hover bordered className="align-middle mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th className="align-middle text-center" style={{ width: "56px" }}>
                                <Input
                                    type="checkbox"
                                    checked={areAllVisibleProductsSelected}
                                    onChange={(e) => toggleSelectVisibleProducts(e.target.checked)}
                                />
                            </th>
                            <th className="align-middle">
                                <FaBox className="me-2 text-primary" size={12} />
                                {t("product.category")}
                            </th>
                            <th className="align-middle">
                                <FaBarcode className="me-2 text-primary" size={12} />
                                {t("product.code")}
                            </th>
                            <th className="align-middle">
                                <FaBarcode className="me-2 text-primary" size={12} />
                                {t("product.barcode")}
                            </th>
                            <th className="align-middle">
                                <FaSearch className="me-2 text-primary" size={12} />
                                {t("product.description")}
                            </th>
                            <th className="align-middle">
                                {t("devis.unite")}
                            </th>
                            <th className="align-middle text-end">
                                <FaTag className="me-2 text-primary" size={12} />
                                {t("product.salePrice")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {!products || products.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center py-4">
                                    <div className="text-muted">{t("product.noProducts")}</div>
                                </td>
                            </tr>
                        ) : (
                            products.map((product) => (
                                <tr key={product.id}>
                                    <td className="align-middle text-center">
                                        <Input
                                            type="checkbox"
                                            checked={Boolean(selectedProducts[product.id])}
                                            onChange={() => toggleSelectProduct(product)}
                                        />
                                    </td>
                                    <td className="align-middle">{product.categorieLibelle}</td>
                                    <td className="align-middle">{product.code}</td>
                                    <td className="align-middle">{product.codeBarre}</td>
                                    <td className="align-middle">{product.libelle}</td>
                                    <td>{unites.find((u) => u.code === product.unite)?.libelle}</td>
                                    <td className="align-middle text-end">{formatPrice(product.prixVenteTTC1)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                        {t("product.showing", {
                            start: (currentPage - 1) * pageSize + 1,
                            end: Math.min(currentPage * pageSize, totalElements),
                            total: totalElements,
                            currentPage,
                            totalPages,
                        })}
                    </small>
                </div>

                <LabelGenerationModal
                    isOpen={labelFormModal}
                    toggle={() => setLabelFormModal(false)}
                    selectedProducts={selectedProductList}
                    filters={filters}
                />
            </CardBody>
        </Card>
    );
};

export default LabelGenerationPage;
